/* eslint-disable @typescript-eslint/no-explicit-any */
// @ts-nocheck - This file is a work in progress and is not fully implemented
import { Delete, CloudUpload } from '@mui/icons-material';
import { Button, Card, CardMedia, IconButton, Box } from '@mui/material';
import React, { useEffect, useState, useRef } from 'react';
import {
  Stage,
  Layer,
  Image as KonvaImage,
  Transformer,
  Group,
  Shape,
} from 'react-konva';
import { TProductImageBuilder } from './PhoneCaseBuilder.types';
import './PhoneCaseBuilder.styled.scss';

const MAX_IMAGES = 1;

export const PhoneCaseBuilder: TProductImageBuilder = ({
  template: { template: phoneCasePath, clip: clipFunc },
}) => {
  const CANVAS_WIDTH = 600;
  const CANVAS_HEIGHT = 600;
  const TEMPLATE_SIZE = 300;
  const TEMPLATE_X = CANVAS_WIDTH / 2 - TEMPLATE_SIZE / 2;
  const TEMPLATE_Y = CANVAS_HEIGHT / 2 - TEMPLATE_SIZE / 2;

  const stageRef = useRef<Stage>(null);
  const transformerRef = useRef<any>(null);
  const imageRefs = useRef<{ [key: string]: any }>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [images, setImages] = useState<
    {
      id: string;
      src: string;
      x: number;
      y: number;
      width: number;
      height: number;
      rotation?: number;
      locked: boolean;
      imgObj?: HTMLImageElement;
    }[]
  >([]);

  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Delete') {
        removeImage(selectedId);
      }
      if (e.key === 'Escape') {
        setSelectedId(null);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedId]);
  // Load image objects once

  useEffect(() => {
    images.forEach((img, index) => {
      if (!img.imgObj && img.src) {
        const newImg = new window.Image();
        newImg.src = img.src;
        newImg.crossOrigin = 'anonymous';
        newImg.onload = () => {
          setImages((prev) => {
            const copy = [...prev];
            if (copy[index]) {
              copy[index].imgObj = newImg;
            }
            return copy;
          });
        };
        selectImage(img.id);
        newImg.onerror = () => {
          console.error('Failed to load image:', img.src);
        };
      }
    });
  }, [images]);

  const selectImage = React.useCallback(
    (id: string) => {
      const img = images.find((i) => i.id === id);

      if (!img) return;
      setSelectedId(id);
    },
    [images]
  );

  const updateImage = (id: string, attrs: any) => {
    setImages((imgs) =>
      imgs.map((i) => (i.id === id ? { ...i, ...attrs } : i))
    );
  };

  const removeImage = (id: string) => {
    setImages((prev) => prev.filter((img) => img.id !== id));
    if (selectedId === id) {
      setSelectedId(null);
    }
    delete imageRefs.current[id];
  };

  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const remainingSlots = MAX_IMAGES - images.length;
    const filesToProcess = files.slice(0, remainingSlots);

    if (filesToProcess.length < files.length) {
      alert(
        `You can only upload up to ${MAX_IMAGES} images. Only the first ${filesToProcess.length} will be added.`
      );
    }

    filesToProcess.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        const newId = 'img_' + Date.now() + '_' + Math.random();
        setImages((prev) => [
          ...prev,
          {
            id: newId,
            src: reader.result as string,
            x: TEMPLATE_X + 40,
            y: TEMPLATE_Y + 40,
            width: 200,
            height: 200,
            rotation: 0,
            locked: false,
          },
        ]);
      };

      reader.readAsDataURL(file);
    });

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const exportImage = () => {
    if (!stageRef.current) return;
    const uri = stageRef.current.toDataURL({ pixelRatio: 2 });
    const a = document.createElement('a');
    a.href = uri;
    a.download = 'phone-case-design.png';
    a.click();
  };

  // Update transformer when selectedId changes
  useEffect(() => {
    if (selectedId && transformerRef.current && imageRefs.current[selectedId]) {
      transformerRef.current.nodes([imageRefs.current[selectedId]]);
      transformerRef.current.getLayer().batchDraw();
    }
  }, [selectedId]);

  return (
    <div className="phone-case-builder">
      {/* Left Section - Canvas */}
      <div className="phone-case-builder__form-section">
        <Card className="phone-case-builder__form-section__card">
          <h6 className="phone-case-builder__form-section__title">
            Upload Images
          </h6>

          <Box className="phone-case-builder__form-section__upload-area">
            <Button
              variant="outlined"
              component="label"
              fullWidth
              startIcon={<CloudUpload />}
              disabled={images.length >= MAX_IMAGES}
            >
              {images.length >= MAX_IMAGES
                ? 'Maximum images reached'
                : `Upload Images (${images.length}/${MAX_IMAGES})`}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                hidden
                onChange={handleUpload}
                disabled={images.length >= MAX_IMAGES}
              />
            </Button>
          </Box>

          {images.length > 0 && (
            <>
              <p className="phone-case-builder__form-section__image-count body2">
                {images.length} of {MAX_IMAGES} images uploaded
              </p>

              <div className="phone-case-builder__form-section__images-grid">
                {images.map((img) => (
                  <Card
                    key={img.id}
                    className="phone-case-builder__form-section__image-card"
                    sx={{ position: 'relative' }}
                  >
                    <CardMedia
                      component="img"
                      image={img.src}
                      sx={{ objectFit: 'cover' }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => removeImage(img.id)}
                      sx={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        background: 'rgba(0,0,0,0.6)',
                        color: '#fff',
                        '&:hover': {
                          background: 'rgba(211, 47, 47, 0.8)',
                        },
                      }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                    {selectedId === img.id && (
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 4,
                          left: 4,
                          right: 4,
                          background: 'rgba(255, 111, 0, 0.9)',
                          color: '#fff',
                          fontSize: '10px',
                          padding: '2px 4px',
                          borderRadius: '4px',
                          textAlign: 'center',
                        }}
                      >
                        Selected
                      </Box>
                    )}
                  </Card>
                ))}
              </div>
            </>
          )}

          <Box className="phone-case-builder__form-section__instructions">
            <p className="phone-case-builder__form-section__instructions__title body2">
              Instructions
            </p>
            <ul className="phone-case-builder__form-section__instructions__list">
              <li>Upload up to {MAX_IMAGES} images</li>
              <li>Click on an image in the preview to select it</li>
              <li>Drag and resize images on the canvas</li>
              <li>Use corner handles to transform images</li>
              <li>Click and drag to rotate images</li>
              <li>Export your design when finished</li>
            </ul>
          </Box>
        </Card>
      </div>
      <div className="phone-case-builder__canvas-section">
        <div className="phone-case-builder__canvas-section__canvas-container">
          <Stage
            ref={stageRef}
            width={CANVAS_WIDTH}
            height={CANVAS_HEIGHT}
            onMouseDown={(e) => {
              if (e.target === e.target.getStage()) setSelectedId(null);
            }}
            onMouseLeave={() => setSelectedId(null)}
            onTouchStart={(e) => {
              if (e.target === e.target.getStage()) setSelectedId(null);
            }}
          >
            <Layer>
              {/* Draw phone case outline with camera cutouts */}
              <Shape sceneFunc={phoneCasePath} />

              {/* Clip group for user images */}
              <Group clipFunc={clipFunc}>
                {images.map((img) => (
                  <KonvaImage
                    key={img.id}
                    // eslint-disable-next-line no-return-assign
                    ref={(node) => (imageRefs.current[img.id] = node)}
                    image={img.imgObj || null}
                    x={img.x}
                    y={img.y}
                    width={img.width}
                    height={img.height}
                    rotation={img.rotation || 0}
                    draggable
                    onClick={() => selectImage(img.id)}
                    onTap={() => selectImage(img.id)}
                    onDragEnd={(e) =>
                      updateImage(img.id, {
                        x: e.target.x(),
                        y: e.target.y(),
                      })
                    }
                    onTransformEnd={() => {
                      const node = imageRefs.current[img.id];
                      const scaleX = node.scaleX();
                      const scaleY = node.scaleY();
                      updateImage(img.id, {
                        x: node.x(),
                        y: node.y(),
                        width: node.width() * scaleX,
                        height: node.height() * scaleY,
                        rotation: node.rotation(),
                      });
                      node.scaleX(1);
                      node.scaleY(1);
                    }}
                  />
                ))}
              </Group>

              {/* Transformer */}
              {selectedId && imageRefs.current[selectedId] && (
                <Transformer
                  ref={transformerRef}
                  rotateEnabled
                  keepRatio={false}
                  enabledAnchors={[
                    'top-left',
                    'top-right',
                    'bottom-left',
                    'bottom-right',
                  ]}
                  borderStroke="red"
                  borderStrokeWidth={3}
                  // add anchors
                  anchorFill="red"
                  anchorStroke="red"
                  anchorStrokeWidth={2}
                  anchorSize={20}
                  // make all anchors look like circles
                  anchorCornerRadius={50}
                />
              )}
            </Layer>
          </Stage>
        </div>
        <Button
          variant="contained"
          color="primary"
          onClick={exportImage}
          className="phone-case-builder__canvas-section__export-button"
          disabled={images.length === 0}
        >
          Export PNG
        </Button>
      </div>

      {/* Right Section - Form */}
    </div>
  );
};
